import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Card,
  Row,
  Col,
  ListGroup,
  Button,
  Spinner,
  Alert,
  Image,
  Tabs,
  Tab,
  Table,
  Badge,
  Modal,
  Form
} from "react-bootstrap";
import api from "../utils/api";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'bootstrap/dist/css/bootstrap.min.css';
import AdminHoursManager from '../components/AdminHoursManager';

// This component is used to generate the PDF content, rendered off-screen
const PrintableBookingDetails = React.forwardRef(({ booking }, ref) => {
  if (!booking) return null;

  return (
    <div ref={ref} className="p-4" style={{ padding: '2rem' }}>
      <h3 className="text-center mb-4">Booking Confirmation Report</h3>
      <Table striped bordered responsive>
        <tbody>
          <tr>
            <td><strong>User Name:</strong></td>
            <td>{booking.userName}</td>
          </tr>
          <tr>
            <td><strong>User Email:</strong></td>
            <td>{booking.userEmail}</td>
          </tr>
          <tr>
            <td><strong>Seat Number:</strong></td>
            <td>{booking.seatNumber}</td>
          </tr>
          {/* Fix: Display the seatTitle from the booking data */}
          <tr>
            <td><strong>Slot Name:</strong></td>
            <td>{booking.slotName}</td>
          </tr>
          <tr>
            <td><strong>Start Time:</strong></td>
            <td>{new Date(booking.startTime).toLocaleString()}</td>
          </tr>
          <tr>
            <td><strong>End Time:</strong></td>
            <td>{new Date(booking.endTime).toLocaleString()}</td>
          </tr>
          <tr>
            <td><strong>Status:</strong></td>
            <td>{booking.status}</td>
          </tr>
        </tbody>
      </Table>

      <h5 className="mt-4">Payment Screenshot</h5>
      <Image src={booking.paymentScreenshot} style={{ maxWidth: '400px' }} fluid />
    </div>
  );
});

// Main Admin Dashboard Component
const AdminDashboard = () => {
  const [pendingBookings, setPendingBookings] = useState([]);
  const [activeBookings, setActiveBookings] = useState([]); // New state for active bookings
  const [allUsers, setAllUsers] = useState([]);
  const [allSeats, setAllSeats] = useState([]);
  const [allPrices, setAllPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bookingForDownload, setBookingForDownload] = useState(null);
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingSeat, setEditingSeat] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);

  const componentRef = useRef();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingsResponse, allUsersResponse, allSeatsResponse, allPricesResponse] = await Promise.all([
        api.get("/admin/bookings-summary"), // Use the new combined API endpoint
        api.get("/admin/users"),
        api.get("/seats"),
        api.get("/prices")
      ]);

      const now = new Date();
      // Filter the single response into pending and active bookings
      const pending = bookingsResponse.data.filter(b => b.status === 'Pending');
      const active = bookingsResponse.data.filter(b => b.status === 'Active' && new Date(b.endTime) > now);

      setPendingBookings(pending);
      setActiveBookings(active); // Set the new state
      setAllUsers(allUsersResponse.data);
      if (allSeatsResponse.data && Array.isArray(allSeatsResponse.data.seats)) {
        setAllSeats(allSeatsResponse.data.seats);
      } else {
        setAllSeats([]);
      }
      setAllPrices(allPricesResponse.data);
    } catch (err) {
      setError("Failed to fetch dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (bookingData) => {
    try {
        const approvedBookingData = { ...bookingData, status: 'Approved by Admin' };
        setBookingForDownload(approvedBookingData);

        await new Promise(resolve => setTimeout(resolve, 500));

        const input = componentRef.current;
        if (input) {
            const image = input.querySelector('img');
            if (image && !image.complete) {
                await new Promise(resolve => {
                    image.onload = resolve;
                    image.onerror = resolve;
                });
            }

            const canvas = await html2canvas(input, {
                scale: 2,
                useCORS: true,
            });
            
            const imgData = canvas.toDataURL('image/jpeg', 1.0);
            
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            });
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            
            pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Booking_Report_${bookingData.bookingId}.pdf`);

            await api.post("/admin/approve", { 
                seatId: bookingData.seatId, 
                bookingId: bookingData.bookingId 
            });

            fetchData();
            setBookingForDownload(null);
        }
    } catch (err) {
        console.error("Failed to generate or download PDF:", err);
        setError("Failed to generate PDF or approve booking.");
    }
  };

  const handleReject = async (seatId, bookingId) => {
    try {
      await api.post("/admin/reject", { seatId, bookingId });
      fetchData();
    } catch (err) {
      setError("Failed to reject booking.");
    }
  };

  const handleSeatModalClose = () => {
    setShowSeatModal(false);
    setEditingSeat(null);
  };

  const handleSeatSave = async (seatData) => {
    try {
      if (editingSeat) {
        await api.put(`/admin/seats/${editingSeat._id}`, seatData);
      } else {
        await api.post('/admin/seats', seatData);
      }
      fetchData();
      handleSeatModalClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save seat.');
    }
  };

  const handleDeleteSeat = async (seatId) => {
    if (window.confirm('Are you sure you want to delete this seat?')) {
      try {
        await api.delete(`/admin/seats/${seatId}`);
        fetchData();
      } catch (err) {
        setError('Failed to delete seat.');
      }
    }
  };

  const handlePriceModalClose = () => {
    setShowPriceModal(false);
    setEditingPrice(null);
  };

  const handlePriceSave = async (priceData) => {
    try {
      if (editingPrice) {
        await api.put(`/admin/prices/${editingPrice._id}`, priceData);
      } else {
        await api.post('/admin/prices', priceData);
      }
      fetchData();
      handlePriceModalClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save price.');
    }
  };

  const handleDeletePrice = async (priceId) => {
    if (window.confirm('Are you sure you want to delete this price slot?')) {
      try {
        await api.delete(`/admin/prices/${priceId}`);
        fetchData();
      } catch (err) {
        setError('Failed to delete price.');
      }
    }
  };

  if (loading)
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
      </Container>
    );
  if (error)
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );

  return (
    <Container className="my-5">
      <h2 className="text-center mb-4">Admin Dashboard</h2>

      <div style={{ position: 'absolute', left: '-9999px', width: '210mm' }}>
        <PrintableBookingDetails ref={componentRef} booking={bookingForDownload} />
      </div>

      <Tabs
        defaultActiveKey="bookings"
        id="admin-dashboard-tabs"
        className="mb-3"
      >
        <Tab eventKey="bookings" title="Pending Bookings">
          <Card className="shadow-sm">
            <Card.Header>Pending Booking Approvals</Card.Header>
            <Card.Body>
              {pendingBookings.length === 0 ? (
                <Alert variant="success">No pending bookings to approve.</Alert>
              ) : (
                <ListGroup variant="flush">
                  {pendingBookings.map((booking) => (
                    <ListGroup.Item key={booking.bookingId}>
                      <Row className="align-items-center">
                        <Col md={3}>
                          <h5>Seat {booking.seatNumber}</h5>
                          <p className="mb-0"><strong>Title:</strong> {booking.seatTitle}</p>
                          <p className="mb-0"><strong>Slot:</strong> {booking.slotName}</p>
                          <small>User: {booking.userName}</small>
                        </Col>
                        <Col md={4}>
                          <p className="mb-0">
                            From: {new Date(booking.startTime).toLocaleString()}
                          </p>
                          <p className="mb-0">
                            To: {new Date(booking.endTime).toLocaleString()}
                          </p>
                        </Col>
                        <Col md={2}>
                          {booking.paymentScreenshots && (
                            <div className="d-flex flex-column align-items-center">
                              <a
                                href={booking.paymentScreenshots}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Image
                                  src={booking.paymentScreenshots}
                                  style={{ maxWidth: "100px" }}
                                  fluid
                                />
                              </a>
                              <a
                                href={booking.paymentScreenshots}
                                download
                                className="mt-2 btn btn-sm btn-outline-secondary"
                              >
                                Download
                              </a>
                            </div>
                          )}
                        </Col>
                        <Col md={3} className="text-end">
                          <Button
                            variant="success"
                            className="me-2"
                            onClick={() => handleDownloadPdf(booking)}
                          >
                            Approve & Download PDF
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() =>
                              handleReject(booking.seatId, booking.bookingId)
                            }
                          >
                            Reject
                          </Button>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="active-bookings" title="Active Bookings">
          <Card className="shadow-sm">
            <Card.Header>Currently Active Seats</Card.Header>
            <Card.Body>
              {activeBookings.length === 0 ? (
                <Alert variant="info">No seats are currently active.</Alert>
              ) : (
                <ListGroup variant="flush">
                  {activeBookings.map((booking) => (
                    <ListGroup.Item key={booking.bookingId}>
                      <Row className="align-items-center">
                        <Col md={3}>
                          <h5>Seat {booking.seatNumber}</h5>
                          <p className="mb-0"><strong>Title:</strong> {booking.seatTitle}</p>
                          <p className="mb-0"><strong>User:</strong> {booking.userName}</p>
                          <small>Email: {booking.userEmail}</small>
                        </Col>
                        <Col md={5}>
                          <p className="mb-0">
                            From: {new Date(booking.startTime).toLocaleString()}
                          </p>
                          <p className="mb-0">
                            To: {new Date(booking.endTime).toLocaleString()}
                          </p>
                          <small>Slot: {booking.slotName}</small>
                        </Col>
                        <Col md={2}>
                          <Badge bg="success">Active</Badge>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="users" title="All Users">
          <Card className="shadow-sm">
            <Card.Header>User Accounts</Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile No</th>
                    <th>Subscription Status</th>
                    <th>Role</th>
                  </tr>
                </thead>
                <tbody>
                  {allUsers.map((user) => (
                    <tr key={user._id}>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.phone}</td>
                      <td>
                        <Badge
                          bg={
                            user.subscription?.isSubscriptionActive
                              ? "success"
                              : "secondary"
                          }
                        >
                          {user.subscription?.isSubscriptionActive
                            ? "Active"
                            : "Inactive"}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={user.isAdmin ? "primary" : "secondary"}>
                          {user.isAdmin ? "Admin" : "User"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="seats" title="Manage Seats">
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              Seat Management
              <Button variant="primary" onClick={() => {
                setEditingSeat(null);
                setShowSeatModal(true);
              }}>
                Create Seat
              </Button>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Seat</th>
                    <th>Schedules</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allSeats.map((seat) => (
                    <tr key={seat._id}>
                      <td>
                        <strong>Seat {seat.seatNumber}</strong>: {seat.title}
                      </td>
                      <td>
                        {seat.schedules && seat.schedules.map(scheduleId => {
                          const price = allPrices.find(p => p._id === scheduleId);
                          return price ? <Badge key={scheduleId} bg="info" className="me-1">{price.label}</Badge> : null;
                        })}
                      </td>
                      <td>
                        <Button variant="outline-primary" className="me-2" onClick={() => {
                          setEditingSeat(seat);
                          setShowSeatModal(true);
                        }}>Edit</Button>
                        <Button variant="outline-danger" onClick={() => handleDeleteSeat(seat._id)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="prices" title="Manage Prices">
          <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center">
              Price Management
              <Button variant="primary" onClick={() => {
                setEditingPrice(null);
                setShowPriceModal(true);
              }}>
                Create Price
              </Button>
            </Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Label</th>
                    <th>Price</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {allPrices.map((price) => (
                    <tr key={price._id}>
                      <td>{price.label}</td>
                      <td>₹{price.price}</td>
                      <td>
                        <Button variant="outline-primary" className="me-2" onClick={() => {
                          setEditingPrice(price);
                          setShowPriceModal(true);
                        }}>Edit</Button>
                        <Button variant="outline-danger" onClick={() => handleDeletePrice(price._id)}>Delete</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>
        <Tab eventKey="hours" title="Manage Hours">
          <AdminHoursManager />
        </Tab>
      </Tabs>

      {showSeatModal && (
        <SeatModal 
          show={showSeatModal} 
          handleClose={handleSeatModalClose} 
          seat={editingSeat} 
          onSave={handleSeatSave} 
          allPrices={allPrices}
        />
      )}

      {showPriceModal && (
        <PriceModal 
          show={showPriceModal} 
          handleClose={handlePriceModalClose} 
          price={editingPrice} 
          onSave={handlePriceSave} 
        />
      )}
    </Container>
  );
};

// Seat Management Modal
const SeatModal = ({ show, handleClose, seat, onSave, allPrices }) => {
  const [seatNumber, setSeatNumber] = useState(seat ? seat.seatNumber : '');
  const [title, setTitle] = useState(seat ? seat.title : '');
  const [selectedSchedules, setSelectedSchedules] = useState(seat ? seat.schedules : []);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ seatNumber, title, schedules: selectedSchedules });
  };

  const handleScheduleChange = (e) => {
    const options = e.target.options;
    const selected = [];
    let newTitle = '';
  
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
  
    if (selected.length > 0) {
      const firstSelectedPrice = allPrices.find(p => p._id === selected[0]);
      if (firstSelectedPrice) {
        newTitle = firstSelectedPrice.label;
      }
    }
  
    setSelectedSchedules(selected);
    setTitle(newTitle);
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{seat ? 'Edit Seat' : 'Create Seat'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Seat Number</Form.Label>
            <Form.Control 
              type="number" 
              value={seatNumber} 
              onChange={(e) => setSeatNumber(e.target.value)} 
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Seat Title</Form.Label>
            <Form.Control 
              type="text" 
              value={title} 
              onChange={(e) => setTitle(e.target.value)} 
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Assign Schedules</Form.Label>
            <Form.Control 
              as="select" 
              multiple 
              onChange={handleScheduleChange}
              value={selectedSchedules}
              required
            >
              {allPrices.map(price => (
                <option key={price._id} value={price._id}>
                  {price.label} (₹{price.price})
                </option>
              ))}
            </Form.Control>
            <Form.Text muted>
              Hold down `Ctrl` (Windows) or `Cmd` (Mac) to select multiple schedules.
            </Form.Text>
          </Form.Group>
          <Button variant="primary" type="submit">
            Save
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

// Price Management Modal
const PriceModal = ({ show, handleClose, price, onSave }) => {
  const [formData, setFormData] = useState({
    slotName: price ? price.slotName : '',
    label: price ? price.label : '',
    duration: price ? price.duration : '',
    price: price ? price.price : '',
    startTimeHour: price ? price.startTimeHour : '',
    endTimeHour: price ? price.endTimeHour : '',
    isSubscription: price ? price.isSubscription : false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{price ? 'Edit Price' : 'Create Price'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Slot Name (e.g., 'full_day')</Form.Label>
            <Form.Control 
              type="text" 
              name="slotName" 
              value={formData.slotName} 
              onChange={handleChange} 
              required 
              readOnly={!!price}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Label</Form.Label>
            <Form.Control 
              type="text" 
              name="label" 
              value={formData.label} 
              onChange={handleChange} 
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Price</Form.Label>
            <Form.Control 
              type="number" 
              name="price" 
              value={formData.price} 
              onChange={handleChange} 
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Duration (in hours)</Form.Label>
            <Form.Control 
              type="number" 
              name="duration" 
              value={formData.duration} 
              onChange={handleChange} 
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Start Time (24h format)</Form.Label>
            <Form.Control 
              type="number" 
              name="startTimeHour" 
              value={formData.startTimeHour} 
              onChange={handleChange} 
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>End Time (24h format)</Form.Label>
            <Form.Control 
              type="number" 
              name="endTimeHour" 
              value={formData.endTimeHour} 
              onChange={handleChange} 
              required 
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check 
              type="checkbox" 
              name="isSubscription" 
              label="Is Subscription?" 
              checked={formData.isSubscription} 
              onChange={handleChange}
            />
          </Form.Group>
          <Button variant="primary" type="submit">
            Save
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
};


export default AdminDashboard;